import Big from "big.js";
import queryString from "query-string";
import ApiDefined from "@appInterfaces/general.types";
import moment from "moment";
import { pushNotify } from "@appComponents/Notification/Notification";
import { getObjectProperty } from "@features/utils/reactFunctions";
import { fiatCurrenciesSlug } from "@features/staticData/fiatCurrencies";
import { AxiosError } from "axios";
import { MULTIPLE_SLASHES } from "./regexpDefinitions";
import { MAIN_CURRENCY, MAIN_CURRENCY_LABEL } from "@bundles/consts";
import { ETiers } from "@appComponents/components/IdentityStatusLabel/IIdentityStatusLabel";

import * as WAValidator from "wallet-address-validator";

export const numberWithDelimiter = (x: number | string, delimiter = ",") => {
  const parts = x.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, delimiter);
  return parts.join(".");
};

export const isAndroidDevice = () => {
  return window.navigator.userAgent.includes("Android");
};

export const daysBetweenDates = (date1: Date, date2: Date) => {
  const a = moment(date1);
  const b = moment(date2);
  return a.diff(b, "days");
};

export const formatServerErrors = (errors: {
  [key: string]: string[];
}): string => {
  if (errors === undefined) return "An error occurred";
  const keys = Object.keys(errors);
  const errorsArray = keys.map((key: string) => {
    return `${key
      .replace(/_/g, " ")
      .replace(/^\w/, (c) => c.toUpperCase())} - ${
      Array.isArray(errors[key]) ? errors[key].join(", ") : errors[key]
    }`;
  });

  return errorsArray.join("<br/>");
};

export const formatDate = (date: string, withTime: boolean = false) => {
  return withTime
    ? moment(date).format("D\xa0MMM\xa0YYYY\xa0HH:mm")
    : moment(date.slice(0, 10), "YYYY-MM-DD", true).format("D\xa0MMM\xa0YYYY");
};

export const deepCopy = (o: any) => {
  let output: any, v, key;
  output = Array.isArray(o) ? [] : {};
  for (key in o) {
    v = o[key];
    output[key] = typeof v === "object" && v !== null ? deepCopy(v) : v;
  }
  return output;
};

export const compareDates = (
  a: { date: string },
  b: { date: string }
): number => {
  const firstDate = new Date(a.date).valueOf();
  const secondDate = new Date(b.date).valueOf();
  return firstDate < secondDate ? 1 : firstDate > secondDate ? -1 : 0;
};

export const splitStrings = (str: string, delimeter: string): string => {
  return str.split(delimeter).join(" ");
};

export const checkAddress = (
  address: string,
  currency: string,
  network?: "erc20"
): string | null => {
  if (network) {
    const addressIsValid = WAValidator.validate(address, "ETH");
    const belongsToOtherNetwork = WAValidator.validate(address, "BTC");

    if (!addressIsValid && !belongsToOtherNetwork) {
      return `Wrong wallet address for ${network.toUpperCase()} network.`;
    }

    return belongsToOtherNetwork
      ? "It seems that provided wallet address does not belong to declared network (Ethereum)."
      : null;
  }

  return null;
};

export const toUpper = (text: string) =>
  text.replace(/^\w/, (c) => c.toUpperCase());

export const numberFormatter = (
  num: string | number,
  digits: number = 2,
  decimal: boolean = false,
  noFixed: boolean = false
): string => {
  if (num === "") return "";

  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  const si = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "k" },
    { value: 1e6, symbol: "M" },
    { value: 1e9, symbol: "B" },
    { value: 1e12, symbol: "T" },
    { value: 1e15, symbol: "P" },
    { value: 1e18, symbol: "E" },
  ];

  let i;
  for (i = si.length - 1; i > 0; i -= 1) {
    if (Number(num) >= si[i].value) {
      break;
    }
  }

  const pureValue = Number(num) / si[i].value;

  if (decimal && Number(num) < 1000) {
    const value = noFixed ? pureValue : pureValue.toFixed(digits);
    return value.toString();
  }

  return pureValue.toFixed(2).replace(rx, "$1") + si[i].symbol;
};

export const toFloorFixed = (num: number, fixed: number) => {
  const re = new RegExp(`^-?\\d+(?:\.\\d{0,${fixed || -1}})?`);
  const match = num.toString().match(re);

  return match ? match[0] : num;
};

export const displayURL = (url: string) => {
  const pattern = /^((http|https):\/\/)/;

  if (!pattern.test(url)) return "http://" + url;

  return url;
};

export const queryPage = (query: string): number => {
  const parsed = parseInt(queryString.parse(query).page, 10);
  if (isNaN(parsed)) return 1;
  return parsed;
};

export const querySearch = (query: string): string => {
  return queryString.parse(query).query || "";
};

export const queryFilter = (query: string): string => {
  return queryString.parse(query).filter || "";
};

export const updateQueryParam = (
  query: string,
  name: string,
  value: number | string
): string => {
  const parsed = queryString.parse(query);

  if (parsed[name]) {
    parsed[name] = value;
    return Object.keys(parsed).reduce((paramsString, param, i) => {
      return paramsString
        ? `${paramsString}&${param}=${Object.values(parsed)[i]}`
        : `?${param}=${Object.values(parsed)[i]}`;
    }, "");
  }

  return query.length > 0 ? `${query}&${name}=${value}` : `?${name}=${value}`;
};

export const animateScrollTo = (
  clicked: any = null,
  element: Element | null = null,
  additionalMargin = 0,
  focus = false
) => {
  let interval: number;
  let actualScrollTop =
    document.documentElement.scrollTop || document.body.scrollTop;
  const menuHeight = 70;
  const clickedTarget = clicked
    ? document.querySelector(
        clicked.currentTarget.getAttribute("href").replace("/", "")
      )
    : element;

  if (clickedTarget) {
    const targetTop =
      clickedTarget.getBoundingClientRect().top +
      actualScrollTop -
      menuHeight -
      additionalMargin;
    const speed = ((actualScrollTop - targetTop) / 50) * -1;

    if (clicked) clicked.preventDefault();

    interval = window.setInterval(() => {
      actualScrollTop += speed;
      window.scrollTo(0, actualScrollTop);

      if (
        (actualScrollTop >= targetTop &&
          actualScrollTop <= targetTop + speed) ||
        (actualScrollTop <= targetTop && actualScrollTop >= targetTop + speed)
      ) {
        window.scrollTo(0, targetTop);
        if (focus) {
          clickedTarget.focus();
        }
        clearInterval(interval);
      }
    }, 10);
  }
};

export const textAutoExpand = (field: HTMLElement) => {
  field.style.height = "inherit";

  const computed = window.getComputedStyle(field);

  const height =
    parseInt(computed.getPropertyValue("border-top-width"), 10) +
    parseInt(computed.getPropertyValue("padding-top"), 10) +
    field.scrollHeight +
    parseInt(computed.getPropertyValue("padding-bottom"), 10) +
    parseInt(computed.getPropertyValue("border-bottom-width"), 10);

  field.style.height = height + "px";
};

export const isEmail = (email: string) => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return emailPattern.test(email);
};

export const shortenText = (text: string | null, length: number = 120) => {
  return text !== null
    ? text.length >= length
      ? text.slice(0, length) + " ..."
      : text
    : "";
};

export const parseValidationError = (error: IValidationError): string => {
  const errors = [""];
  Object.entries(error.errors).forEach(([key, value]) => {
    errors.push(`${key.replace(/[_]/g, " ").replace(/[.]/g, "-")} : ${value}`);
  });

  return `${error.message}: ${toUpper(errors.join(""))}`;
};

export const isUrl = (text: string) => {
  const urlPattern = new RegExp(
    "^(https?:\\/\\/)?" +
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" +
      "((\\d{1,3}\\.){3}\\d{1,3}))" +
      "(\\:\\d+)?" +
      "(\\/[-a-z\\d%@_.~+&:]*)*" +
      "(\\?[;&a-z\\d%@_.,~+&:=-]*)?" +
      "(\\#[-a-z\\d_]*)?$",
    "i"
  );

  return urlPattern.test(text);
};

export const isImage = (text: string) => {
  return [".jpg", ".png"].some((format) => text.includes(format));
};

export const getYoutubeVideoId = (url: string) => {
  const regExp =
    /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);

  if (match) return match[2];

  return false;
};

export const getVimeoVideoId = (url: string) => {
  const regExp =
    /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/)|(video\/))?([0-9]+)/;
  const match = url.match(regExp);

  if (match) return match[6];

  return false;
};

export const checkImg = (src: string | undefined | null, fallback: string) => {
  return new Promise((resolve, reject) => {
    const tmp: { img?: HTMLImageElement } = {};
    tmp.img = new Image();
    tmp.img.onload = () => resolve(src || undefined);
    tmp.img.onerror = () => reject(fallback);
    tmp.img.src = src ? src : "fail";
  });
};

export const isDefined = (variable: ApiDefined<any>) => {
  return typeof variable !== "undefined";
};

export const catchError = (
  error: AxiosError<any>,
  reload: string | boolean = false,
  message = "",
  time = 5000,
  timeToReload = 0
) => {
  window.scrollTo(0, 0);

  if (error.response && error.response.data.errors) {
    pushNotify("error", formatServerErrors(error.response.data.errors), time);
  } else {
    pushNotify(
      "error",
      message || "Something went wrong. Please try again.",
      time
    );
  }

  setTimeout(() => {
    if (reload === true) {
      location.reload();
    } else if (typeof reload === "string") {
      window.location.href = reload;
    }
  }, timeToReload);
};

export const getMinutesFromTimestamp = (timestamp: number) => {
  return timestamp / 1000 / 60;
};

export const displayBankNumber = (num?: string, separator: string = " ") => {
  return num ? (removeSpaces(num).match(/.{1,4}/g) || []).join(separator) : "";
};

export const removeSpaces = (value: string) => value.split(" ").join("");

export const isFiatCurrency = (currency: string) =>
  fiatCurrenciesSlug.includes(currency);

export function hasNullValues<T>(data: T): boolean {
  for (const key in data) {
    const value = getObjectProperty<T, keyof T>(data, key);
    if (
      typeof value !== "object" &&
      value !== null &&
      value !== "" &&
      value !== "NO DATA"
    ) {
      return false;
    }
  }

  return true;
}

export const normalizeBankAccountNumber = (accountNumber: string): string =>
  accountNumber.replace(/[\s|\u00A0]/, "").trim();

export const isObject = (value: unknown) =>
  Object.prototype.toString.call(value) === "[object Object]";

export const isIsoDate = (value: string) => {
  if (!!Number(value)) {
    return false;
  }

  return !!Date.parse(value);
};

export const getAxiosErrorMessage = (error: AxiosError<any>): string =>
  formatServerErrors(error.response!.data.errors) ||
  error.response!.statusText ||
  error.message ||
  "Something went wrong";

export const countDecimalPlaces = (value: number) => {
  if (new Big(value).mod(1).toString() !== "0") {
    const valueAsString = value.toString();
    if (valueAsString.indexOf("e-") > -1) {
      const [base, trail] = valueAsString.split("e-");
      const trailInt = parseInt(trail, 10);
      const index = base.indexOf(".");

      return index === -1 ? 0 + trailInt : base.length - index - 1 + trailInt;
    }

    return valueAsString.split(".")[1].length || 0;
  }

  return 0;
};

export const isLocalStorageAvailable = () => {
  try {
    const x = "__local_storage_test__";
    localStorage.setItem(x, x);
    localStorage.removeItem(x);
    return true;
  } catch {
    return false;
  }
};

export const formatNumberToUSD = (num: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 0,
  }).format(num);
};

export const formatNumberWithCommas = (num: number, digitsNumber?: number) => {
  const digitsNum = digitsNumber === 0 ? digitsNumber : digitsNumber || 2;

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: digitsNum,
    maximumFractionDigits: digitsNum,
  }).format(num);
};

export const formatNumberWithDynamicCommas = (
  num: number,
  digitsNumber?: number
) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: digitsNumber ?? 0,
    maximumFractionDigits: digitsNumber ?? 4,
  }).format(num);
};

export const capitalize = (word: string): string =>
  word.replace(/^./, word[0].toUpperCase());
export const removeMultipleSlashes = (url: string): string =>
  url.replace(MULTIPLE_SLASHES, "/");

export const renameMainCurrency = (currency: string) =>
  currency === MAIN_CURRENCY ? MAIN_CURRENCY_LABEL : currency;

export const hasRequiredTier = (
  requiredTier: ETiers,
  userTier: string | undefined
) => {
  const convertedTier: ETiers | undefined = ETiers[userTier as any] as any;
  if (!userTier || convertedTier === undefined) return false;

  switch (requiredTier) {
    case ETiers.tier_0:
      return true;
    case ETiers.tier_0_kyc:
      return convertedTier !== ETiers.tier_0;
    case ETiers.tier_1_uk_restricted:
      return ![ETiers.tier_0, ETiers.tier_0_kyc].includes(convertedTier);
    case ETiers.tier_1:
      return ![ETiers.tier_0, ETiers.tier_0_kyc].includes(convertedTier);
    case ETiers.tier_2:
      return ![
        ETiers.tier_0,
        ETiers.tier_0_kyc,
        ETiers.tier_1_uk_restricted,
        ETiers.tier_1,
      ].includes(convertedTier);
    case ETiers.tier_3:
      return [ETiers.tier_3, ETiers.tier_4].includes(convertedTier);
    case ETiers.tier_4:
      return convertedTier === ETiers.tier_4;
  }
  return false;
};
